import { useState } from 'react';
import { activityDaysApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { X, Calendar, AlertCircle } from 'lucide-react';

export default function CreateActivityDayModal({ caseId, existingDates, onClose, onCreated }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Default to today's date
    const today = new Date().toISOString().split('T')[0];
    const [date, setDate] = useState(today);
    const [serviceStart, setServiceStart] = useState('');
    const [serviceEnd, setServiceEnd] = useState('');
    const [transferTime, setTransferTime] = useState('');
    const [operatorsCount, setOperatorsCount] = useState('1');
    const [vehiclesCount, setVehiclesCount] = useState('1');
    const [vehicleType, setVehicleType] = useState('auto');
    const [notes, setNotes] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!date) {
            setError('La data è obbligatoria');
            return;
        }

        if (existingDates.includes(date)) {
            setError('Esiste già una giornata per questa data');
            return;
        }

        try {
            setLoading(true);
            setError('');
            
            const response = await activityDaysApi.create(caseId, {
                date: date,
                service_start_time: serviceStart || null,
                service_end_time: serviceEnd || null,
                transfer_time_minutes: transferTime ? parseInt(transferTime) : null,
                operators_count: parseInt(operatorsCount) || 1,
                vehicles_count: parseInt(vehiclesCount) || 1,
                vehicle_type: vehicleType,
                notes: notes
            });
            
            onCreated(response.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore nella creazione della giornata');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-md">
                {/* Header */}
                <div className="border-b border-white/10 p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-[#00FF41]" />
                        <h2 className="font-heading text-lg font-bold text-white">Nuova Giornata di Attività</h2>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="text-white/60 hover:text-white"
                    >
                        <X className="w-5 h-5" />
                    </Button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {error && (
                        <div className="bg-red-500/10 border border-red-500 p-3 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-red-500" />
                            <span className="text-red-500 text-sm">{error}</span>
                        </div>
                    )}

                    <div>
                        <Label className="text-white/60 uppercase text-xs">Data *</Label>
                        <Input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            required
                            data-testid="day-date-input"
                            className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1 font-mono"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-white/60 uppercase text-xs">Inizio Servizio</Label>
                            <Input
                                type="time"
                                value={serviceStart}
                                onChange={(e) => setServiceStart(e.target.value)}
                                data-testid="day-start-input"
                                className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1 font-mono"
                            />
                        </div>
                        <div>
                            <Label className="text-white/60 uppercase text-xs">Fine Servizio</Label>
                            <Input
                                type="time"
                                value={serviceEnd}
                                onChange={(e) => setServiceEnd(e.target.value)}
                                data-testid="day-end-input"
                                className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1 font-mono"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-white/60 uppercase text-xs">Trasferimento (min)</Label>
                            <Input
                                type="number"
                                value={transferTime}
                                onChange={(e) => setTransferTime(e.target.value)}
                                placeholder="0"
                                data-testid="day-transfer-input"
                                className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1 font-mono"
                            />
                        </div>
                        <div>
                            <Label className="text-white/60 uppercase text-xs">Num. Operatori</Label>
                            <Input
                                type="number"
                                min="1"
                                value={operatorsCount}
                                onChange={(e) => setOperatorsCount(e.target.value)}
                                data-testid="day-operators-input"
                                className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1 font-mono"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-white/60 uppercase text-xs">Num. Automezzi</Label>
                            <Input
                                type="number"
                                min="1"
                                value={vehiclesCount}
                                onChange={(e) => setVehiclesCount(e.target.value)}
                                data-testid="day-vehicles-input"
                                className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1 font-mono"
                            />
                        </div>
                        <div>
                            <Label className="text-white/60 uppercase text-xs">Tipo Automezzo</Label>
                            <select
                                value={vehicleType}
                                onChange={(e) => setVehicleType(e.target.value)}
                                data-testid="day-vehicle-type-select"
                                className="w-full h-10 mt-1 bg-black border border-white/20 text-white focus:border-[#00FF41] rounded-none font-mono px-3"
                            >
                                <option value="auto">Auto</option>
                                <option value="moto">Moto</option>
                                <option value="altro">Altro</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <Label className="text-white/60 uppercase text-xs">Note</Label>
                        <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Note aggiuntive..."
                            rows={3}
                            data-testid="day-notes-input"
                            className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            className="border-white/20 text-white hover:bg-white/10 rounded-none"
                        >
                            ANNULLA
                        </Button>
                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="submit-day-btn"
                            className="bg-[#00FF41] text-black hover:bg-[#00CC33] hover:shadow-neon rounded-none"
                        >
                            {loading ? 'CREAZIONE...' : 'CREA GIORNATA'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
